import { useNavigate } from 'react-router-dom';
import type { MacroAnalysis } from '@shipping/core';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart, BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Cell,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import type { ValueType } from 'recharts/types/component/DefaultTooltipContent';
import type { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { useAnalysisStore } from '../stores/useAnalysisStore';

echarts.use([
  PieChart,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  CanvasRenderer,
]);

const REGION_COLORS = [
  '#FF4D4F', '#FF7A45', '#FFA940', '#FADB14', '#52C41A',
  '#1890FF', '#722ED1', '#EB2F96', '#13C2C2', '#2F54EB',
  '#9254DE', '#F759AB', '#36CFC9', '#597EF7', '#B37FEB',
  '#FFC069', '#87E8DE', '#ADC6FF', '#D3ADF7', '#FFADD2',
  '#95DE64', '#5CDBD3', '#69C0FF', '#85A5FF', '#B37FEB',
];

interface WeightDatum {
  name: string;
  count: number;
  ratio: string;
  fill: string;
}

interface CostLayer {
  label: string;
  amount: number;
  color: string;
  percent: number;
}

export default function MacroDashboard() {
  const navigate = useNavigate();
  const macro = useAnalysisStore((s) => s.macro);

  if (!macro) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="text-6xl">...</div>
        <h2 className="text-xl font-semibold text-gray-700">No macro analysis data</h2>
        <p className="text-gray-400">Upload orders and complete an analysis first.</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2.5 bg-brand text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Back to upload
        </button>
      </div>
    );
  }

  const pieData = macro.regionDistribution.map((region) => ({
    name: `${region.region}${region.isBSGD ? ' *' : ''}`,
    value: region.orderCount,
  }));

  const pieOption = {
    tooltip: {
      trigger: 'item' as const,
      formatter: '{b}: {c} orders ({d}%)',
    },
    legend: {
      type: 'scroll' as const,
      orient: 'vertical' as const,
      right: 0,
      top: 20,
      bottom: 20,
      textStyle: { fontSize: 11 },
    },
    color: REGION_COLORS,
    series: [
      {
        type: 'pie' as const,
        radius: ['35%', '65%'],
        center: ['35%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 4,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: { show: false },
        emphasis: {
          label: { show: true, fontSize: 13, fontWeight: 'bold' as const },
        },
        data: pieData,
      },
    ],
  };

  const weightData: WeightDatum[] = macro.weightDistribution.map((bucket) => ({
    name: bucket.label,
    count: bucket.count,
    ratio: (bucket.ratio * 100).toFixed(1),
    fill: bucket.isOverweight ? '#FF4D4F' : '#1890FF',
  }));

  const costLayers = buildCostLayers(macro);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Macro Report</h1>
          <p className="text-sm text-gray-500 mt-1">
            Hidden cost signals across {macro.totalOrders.toLocaleString()} orders
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ShockCard
          label="BSGD order ratio"
          value={`${(macro.bsgdRatio * 100).toFixed(1)}%`}
          color="text-danger"
          sub={`${macro.bsgdOrders} orders vs ${(macro.industryBsgdRatio * 100).toFixed(0)}% industry`}
        />
        <ShockCard
          label="Hidden surcharge"
          value={`CNY ${macro.hiddenSurcharge.toLocaleString()}`}
          color="text-danger"
          sub={macro.hiddenSurcharge > 1000 ? 'Large enough to matter monthly' : ''}
        />
        <ShockCard
          label="Overweight ratio"
          value={`${(macro.overweightRatio * 100).toFixed(1)}%`}
          color="text-warning"
          sub={`${macro.overweightOrders} orders over 5kg`}
        />
        <ShockCard
          label="Remote surcharge"
          value={`CNY ${macro.remoteSurcharge.toLocaleString()}`}
          color="text-warning"
          sub="Can often be reduced with routing rules"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-base font-medium text-gray-900 mb-2">Destination distribution</h3>
          <p className="text-xs text-gray-400 mb-4">
            Regions marked with * are treated as BSGD destinations.
          </p>
          <div className="h-72">
            <ReactEChartsCore
              echarts={echarts}
              option={pieOption}
              style={{ height: '100%', width: '100%' }}
              notMerge
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-base font-medium text-gray-900 mb-2">Weight distribution</h3>
          <p className="text-xs text-gray-400 mb-4">
            Red buckets indicate the overweight range above 5kg.
          </p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ReBarChart data={weightData} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <ReTooltip formatter={formatWeightTooltip} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={48}>
                  {weightData.map((datum, index) => (
                    <Cell key={index} fill={datum.fill} />
                  ))}
                  <LabelList dataKey="count" position="top" style={{ fontSize: 11, fill: '#666' }} />
                </Bar>
              </ReBarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-base font-medium text-gray-900 mb-2">Cost breakdown</h3>
        <p className="text-xs text-gray-400 mb-5">
          Starting from base freight, then layering in hidden surcharges.
        </p>

        <div className="flex h-10 rounded-lg overflow-hidden mb-6">
          {costLayers.map((layer) => (
            <div
              key={layer.label}
              className="relative group flex items-center justify-center transition-opacity hover:opacity-90"
              style={{ width: `${layer.percent}%`, backgroundColor: layer.color, minWidth: layer.percent > 0 ? 40 : 0 }}
            >
              {layer.percent >= 8 ? (
                <span className="text-white text-xs font-medium truncate px-1">
                  {layer.percent.toFixed(0)}%
                </span>
              ) : null}
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs rounded-md px-2.5 py-1.5 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                {layer.label}: CNY {layer.amount.toLocaleString()} ({layer.percent.toFixed(1)}%)
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          {costLayers.map((layer) => (
            <div key={layer.label} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: layer.color }} />
                <span className="text-sm text-gray-700">{layer.label}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-900">CNY {layer.amount.toLocaleString()}</span>
                <span className="text-xs text-gray-400 w-12 text-right">{layer.percent.toFixed(1)}%</span>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-sm flex-shrink-0 bg-gray-800" />
              <span className="text-sm font-semibold text-gray-900">Total actual cost</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm font-bold text-gray-900">CNY {macro.totalActualCost.toLocaleString()}</span>
              <span className="text-xs text-gray-400 w-12 text-right">100%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-100 p-6">
        <p className="text-gray-700 leading-relaxed">
          You are losing
          {' '}
          <span className="text-danger font-bold text-lg">CNY {macro.hiddenSurcharge.toLocaleString()}</span>
          {' '}
          to hidden freight costs each month. That is roughly
          {' '}
          <span className="font-bold">{Math.round(macro.hiddenSurcharge / 15)}</span>
          {' '}
          extra shipments, or
          {' '}
          <span className="text-danger font-bold text-lg">
            CNY {(macro.hiddenSurcharge * 12 / 10000).toFixed(1)}w
          </span>
          {' '}
          per year.
        </p>
      </div>

      <div className="text-center">
        <button
          onClick={() => navigate('/routing')}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand text-white font-medium rounded-lg hover:bg-blue-600 transition-colors"
        >
          View routing recommendations
          <span>{'->'}</span>
        </button>
      </div>
    </div>
  );
}

function ShockCard({
  label,
  value,
  color,
  sub,
}: {
  label: string;
  value: string;
  color: string;
  sub: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="text-sm text-gray-500">{label}</div>
      <div className={`text-3xl font-bold mt-2 ${color}`}>{value}</div>
      {sub ? <div className="text-xs text-gray-400 mt-1">{sub}</div> : null}
    </div>
  );
}

function buildCostLayers(macro: MacroAnalysis): CostLayer[] {
  const total = macro.totalActualCost || 1;
  const layers: CostLayer[] = [
    {
      label: 'Base freight',
      amount: macro.totalBaseCost,
      color: '#1890FF',
      percent: (macro.totalBaseCost / total) * 100,
    },
  ];

  if (macro.hiddenSurcharge > 0) {
    layers.push({
      label: 'BSGD surcharge',
      amount: macro.hiddenSurcharge,
      color: '#FF4D4F',
      percent: (macro.hiddenSurcharge / total) * 100,
    });
  }

  if (macro.remoteSurcharge > 0) {
    layers.push({
      label: 'Remote surcharge',
      amount: macro.remoteSurcharge,
      color: '#FAAD14',
      percent: (macro.remoteSurcharge / total) * 100,
    });
  }

  return layers;
}

function formatWeightTooltip(
  value: ValueType,
  _name: string,
  item: Payload<ValueType, string>,
): [string, string] {
  const count = typeof value === 'number' ? value : Number(value ?? 0);
  const ratio = typeof item.payload === 'object' && item.payload && 'ratio' in item.payload
    ? String(item.payload.ratio)
    : '0';
  return [`${count} orders (${ratio}%)`, 'Order count'];
}
